import React, { useEffect, useState } from "react";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { InputTextarea } from "primereact/inputtextarea";
import { Button } from "primereact/button";
import { Message } from "primereact/message";

import { listResignReasons } from "../../../../services/onboarding/resignReasons";
import { ResignReason } from "../../../../types/onboarding";

interface ResignDialogProps {
  visible: boolean;
  onHide: () => void;
  onConfirm: (reasonCode: string, note: string) => Promise<void> | void;
}

/**
 * US2: resign a prospect. A reason from the active list is required (FR-013);
 * a free-text note is optional. Resignation is irreversible — the parent shows
 * a confirmation step before calling onConfirm.
 */
const ResignDialog: React.FC<ResignDialogProps> = ({
  visible,
  onHide,
  onConfirm,
}) => {
  const [reasons, setReasons] = useState<ResignReason[]>([]);
  const [reasonCode, setReasonCode] = useState<string | null>(null);
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    if (!visible) return;
    listResignReasons(true)
      .then((res) => setReasons(res?.reasons ?? []))
      .catch(() => setReasons([]));
  }, [visible]);

  const close = () => {
    setReasonCode(null);
    setNote("");
    setConfirming(false);
    onHide();
  };

  const submit = async () => {
    if (!reasonCode) return;
    setSubmitting(true);
    try {
      await onConfirm(reasonCode, note.trim());
      close();
    } finally {
      setSubmitting(false);
    }
  };

  const options = reasons.map((r) => ({
    label: r.display_label,
    value: r.code,
  }));

  const footer = (
    <div>
      <Button label="Cancel" text onClick={close} disabled={submitting} />
      {!confirming ? (
        <Button
          label="Continue"
          severity="danger"
          onClick={() => setConfirming(true)}
          disabled={!reasonCode}
        />
      ) : (
        <Button
          label="Confirm resignation"
          icon="pi pi-times-circle"
          severity="danger"
          onClick={submit}
          loading={submitting}
        />
      )}
    </div>
  );

  return (
    <Dialog
      header="Resign prospect"
      visible={visible}
      onHide={close}
      footer={footer}
      style={{ width: "30rem" }}
    >
      <label htmlFor="resign-reason" className="input-label">
        Reason <span>*</span>
      </label>
      <Dropdown
        inputId="resign-reason"
        value={reasonCode}
        options={options}
        onChange={(e) => setReasonCode(e.value)}
        placeholder="Select a reason"
        className="w-full mt-2 mb-3"
        emptyMessage="No active reasons available."
      />

      <label htmlFor="resign-note" className="input-label">
        Note (optional)
      </label>
      <InputTextarea
        id="resign-note"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        rows={3}
        className="w-full mt-2"
        autoResize
      />

      {confirming && (
        <Message
          severity="warn"
          className="w-full mt-3"
          text="Resignation can't be undone. The prospect will be closed and no further status changes will be possible."
        />
      )}
    </Dialog>
  );
};

export default ResignDialog;
